import { JSONSchema7 } from 'json-schema';
import type { JSONSchema } from 'formhell';
import { isDeepEqual } from '../../utils/isDeepEqual';
import { JSON_POINTER_FORMAT, X_UI_FORMAT } from '../../utils/schemaExtensions';
import { toTitleCase } from '../../utils/stringMethods';

export const DRAFT_2020_12 = 'https://json-schema.org/draft/2020-12/schema';

const SCHEMA_MAP_KEYS = ['properties', 'patternProperties', '$defs', 'definitions', 'dependentSchemas'];
const SCHEMA_KEYS = ['items', 'additionalProperties', 'unevaluatedProperties', 'unevaluatedItems', 'propertyNames', 'contains', 'not', 'if', 'then', 'else'];
const SCHEMA_LIST_KEYS = ['prefixItems', 'allOf', 'anyOf', 'oneOf'];

const isSchemaObject = (node: unknown): node is Record<string, any> =>
    !!node && typeof node === 'object' && !Array.isArray(node);

const isHomogeneousTuple = (items: unknown[]): boolean =>
    items.length > 0 && items.every(item => isDeepEqual(item, items[0]));

const normalizeNode = (node: unknown): unknown => {
    if (!isSchemaObject(node)) {
        return node;
    }
    const out: Record<string, unknown> = { ...node };

    if (Array.isArray(out.items) && out.additionalItems === undefined && isHomogeneousTuple(out.items)) {
        out.items = out.items[0];
    }

    if (Array.isArray(out.items)) {
        out.prefixItems = out.items.map(normalizeNode);
        delete out.items;
        if (out.additionalItems !== undefined) {
            out.items = normalizeNode(out.additionalItems);
        }
        delete out.additionalItems;
    }

    if (isSchemaObject(out.dependencies)) {
        Object.entries(out.dependencies).forEach(([key, dep]) => {
            if (Array.isArray(dep)) {
                out.dependentRequired = { ...(out.dependentRequired || {}), [key]: dep };
            } else {
                out.dependentSchemas = { ...(out.dependentSchemas || {}), [key]: dep };
            }
        });
        delete out.dependencies;
    }

    // FormHell's AJV has no format plugin: the only format the app acts on is json-pointer
    // (select labels), which moves to an extension key; the rest would just log warnings.
    if (typeof out.format === 'string') {
        if (out.format === JSON_POINTER_FORMAT) {
            out[X_UI_FORMAT] = JSON_POINTER_FORMAT;
        }
        delete out.format;
    }

    SCHEMA_MAP_KEYS.forEach(key => {
        const map = out[key];
        if (isSchemaObject(map)) {
            out[key] = Object.fromEntries(
                Object.entries(map).map(([name, child]) => [name, normalizeNode(child)])
            );
        }
    });
    // FormHell labels an untitled property with its raw key; give it a readable title instead.
    if (isSchemaObject(out.properties)) {
        out.properties = Object.fromEntries(Object.entries(out.properties).map(([name, child]) =>
            [name, isSchemaObject(child) && typeof child.title !== 'string' ? { ...child, title: toTitleCase(name) } : child]
        ));
    }
    SCHEMA_KEYS.forEach(key => {
        if (out[key] !== undefined) {
            out[key] = normalizeNode(out[key]);
        }
    });
    SCHEMA_LIST_KEYS.forEach(key => {
        const list = out[key];
        if (Array.isArray(list)) {
            out[key] = list.map(normalizeNode);
        }
    });
    return out;
};

export const normalizeSchemaForFormHell = (schema: JSONSchema7): JSONSchema => ({
    ...(normalizeNode(schema) as JSONSchema),
    $schema: DRAFT_2020_12,
});
